import React, { useCallback, useMemo, useState, useEffect, useRef } from 'react'
import { Formik, Form, useFormikContext, FormikProps } from 'formik'
import { Flex, Box, Text } from 'rebass'
import * as yup from 'yup'
import { useNewTranslation } from 'hooks/index'
import _ from 'lodash'
import { useDispatch, useSelector } from 'react-redux'
import { theme } from 'theme'
import ButtonPrimary from 'components/Shared/Button/OldButton/ButtonPrimary'
import ButtonSecondary from 'components/Shared/Button/OldButton/ButtonSecondary'
import CommentAndVoteSummary from '../ProgressBlock/CommentAndVoteSummary'
import DiscussionList from './DiscussionList'
import { FileSelector, ScrollToError } from './ReviewSubmission'
import {
    selectSelectedCampaignUuid,
    makeSelectedMediaContentsSelector,
    setSelectedMediaContent,
    setSelectedDiscussionData,
    selectSelectedDiscussionData,
    clearSelectedDiscussionData,
    clearSelectedMediaContent,
} from 'state/review'
import { createDiscussSubmission, getDiscussion, updateDiscussSubmission } from 'state/submissions'
import { State } from 'state'
import RectangleButton from 'particles/Button/RectangleButton'
import { spacing } from 'particles/styles'
import styled from 'styled-components'
import { coreColors } from 'particles/colors'
import Inputs from 'components/Inputs'

// Will save state once the form have been destroyed
interface AutoSaveDiscussionDataProps {
    uuid: string
}

const AutoSaveDiscussionData: React.FC<AutoSaveDiscussionDataProps> = ({ uuid }) => {
    const dispatch = useDispatch()
    const reviewData = useRef<any>({})
    const { values, dirty } = useFormikContext()
    useEffect(() => {
        reviewData.current = values
    }, [values])
    useEffect(() => {
        return () => {
            dirty && dispatch(setSelectedDiscussionData({ uuid, data: reviewData.current }))
        }
    }, [dirty])
    return null
}

const INITIAL_VAUES = {
    comment: '',
    selectedDiscussionUuid: '',
    data: {
        files: [],
    },
}

const DiscussSubmission: React.FC<any> = ({ step, submissionProgress }) => {
    const [newT] = useNewTranslation()
    const dispatch = useDispatch()

    const [isFetchDiscussion, setIsFetchDiscussion] = useState<boolean>(false)
    const isCustomStep = step.type === 'custom'

    const selectedCampaignUuid = useSelector(selectSelectedCampaignUuid)
    const selectSelectedMediaContents = useMemo(makeSelectedMediaContentsSelector, [])
    const selectedContent = useSelector((state: State) => selectSelectedMediaContents(state, { submissionProgress }))
    const selectedDiscussionData = useSelector(selectSelectedDiscussionData(submissionProgress.uuid))

    const initialValues = useMemo(() => {
        if (selectedDiscussionData) {
            return selectedDiscussionData
        }
        return {
            INITIAL_VAUES,
            data: {
                files: selectedContent,
            },
        }
    }, [selectedDiscussionData, selectedContent])

    const setSelectedDiscussionContent = useCallback((discussion: any, setFieldValue: any) => {
        setFieldValue('selectedDiscussionUuid', discussion.uuid)
        setFieldValue('comment', discussion.comment)
        dispatch(
            setSelectedMediaContent({
                submissionUuid: submissionProgress.uuid,
                contentUuids: discussion.files.map((file: any) => file.uuid),
            }),
        )
    }, [])

    const clearSelectedDiscussionContent = useCallback(() => {
        dispatch(clearSelectedMediaContent({ submissionUuid: submissionProgress.uuid }))
        dispatch(clearSelectedDiscussionData(submissionProgress.uuid))
    }, [submissionProgress])

    const fetchDiscussion = useCallback(() => {
        if (selectedCampaignUuid) {
            dispatch(
                getDiscussion({
                    campaignUuid: selectedCampaignUuid,
                    submissionUuid: submissionProgress.uuid,
                }),
            )
        }
    }, [selectedCampaignUuid, submissionProgress])

    const handleCreateDiscuss = useCallback(
        (comment: any, files: any) => {
            if (selectedCampaignUuid) {
                dispatch(
                    createDiscussSubmission({
                        campaignUuid: selectedCampaignUuid,
                        submissionUuid: submissionProgress.uuid,
                        comment,
                        files,
                    }),
                )
            }
        },
        [selectedCampaignUuid, submissionProgress],
    )

    const handleUpdateDiscuss = useCallback(
        (comment: any, files: any, selectedDiscussionUuid: string) => {
            if (selectedCampaignUuid) {
                dispatch(
                    updateDiscussSubmission({
                        campaignUuid: selectedCampaignUuid,
                        submissionUuid: submissionProgress.uuid,
                        discussionUuid: selectedDiscussionUuid,
                        comment,
                        files,
                    }),
                )
            }
        },
        [selectedCampaignUuid, submissionProgress],
    )

    const validation = yup.object({
        comment: yup
            .string()
            .nullable()
            .max(10000, ({ max }) =>
                newT(
                    'errorMessage.validation.enterLessThanCharacterLimit',
                    'Please enter less than {{max}} characters.',
                    {
                        max,
                    },
                ),
            )
            .required(newT('errorMessage.validation.enterComment', 'Please enter comment.')),
        data: yup.object({
            files: yup.array().of(yup.string()).nullable(),
        }),
        selectedDiscussionUuid: yup.string(),
    })

    const handleScroll = (e: any) => {
        if (isFetchDiscussion) return
        fetchDiscussion()
        setIsFetchDiscussion(true)
    }

    return (
        <Box>
            <Formik
                initialValues={initialValues}
                validationSchema={validation}
                onSubmit={(values: any, { setSubmitting, resetForm }) => {
                    const filteredFiles = values.data.files.map((file: any) => {
                        const allowed = ['public_id', 'resource_type']
                        const filtered = Object.keys(file)
                            .filter(key => allowed.includes(key))
                            .reduce((obj: any, key: string) => {
                                obj[key] = file[key]
                                return obj
                            }, {})
                        return filtered
                    })
                    if (!_.isEmpty(values.selectedDiscussionUuid)) {
                        handleUpdateDiscuss(values.comment, filteredFiles, values.selectedDiscussionUuid)
                    } else {
                        handleCreateDiscuss(values.comment, filteredFiles)
                    }
                    setSubmitting(false)
                    resetForm({ values: INITIAL_VAUES })
                    clearSelectedDiscussionContent()
                }}
            >
                {(formikBag: FormikProps<any>) => (
                    <Form
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: spacing[200],
                        }}
                    >
                        <ScrollableBox>
                            <Box
                                className="scrollable-list"
                                onScroll={handleScroll}
                                mt={theme.spacing.xxs}
                                overflowY="scroll"
                                minHeight="300px"
                                maxHeight="400px"
                            >
                                <DiscussionList
                                    submissionProgress={submissionProgress}
                                    setSelectedDiscussionContent={(discussion: any) =>
                                        setSelectedDiscussionContent(discussion, formikBag.setFieldValue)
                                    }
                                    selectedDiscussionUuid={formikBag.values.selectedDiscussionUuid}
                                />
                            </Box>
                            <Box m={theme.spacing.xs} sx={{ borderTop: '1px solid #000000' }}>
                                <CommentAndVoteSummary submissionProgress={submissionProgress} step={step} />
                            </Box>
                            <Box
                                px={spacing[200]}
                                sx={{
                                    boxShadow: 'inset 2px 2px 2px rgba(0, 0, 0, 0.25)',
                                    border: '1px solid rgba(0, 0, 0, 0.2)',
                                    borderRadius: '4px',
                                }}
                            >
                                {!isCustomStep && (
                                    <Box>
                                        {formikBag.values.data.files.length == 0 && (
                                            <Text
                                                fontSize="0.6em"
                                                color={theme.colors.doveGrey}
                                                textAlign="center"
                                                mx={theme.spacing.s}
                                                py={theme.spacing.s}
                                            >
                                                {newT(
                                                    'submission.review.selectPhotoInstruction',
                                                    'Choose photos you want to comment from the photo list',
                                                )}
                                            </Text>
                                        )}
                                        <FileSelector
                                            name="data.files"
                                            data={selectedContent}
                                            dragPosX={755}
                                            dragPosY={629}
                                        />
                                    </Box>
                                )}
                                <Box width="100%">
                                    <Inputs.TextArea
                                        className="scrollable-list"
                                        name="comment"
                                        placeholder={newT('placeholderText.enterCommentSubmission')}
                                        minTextAreaHeight={120}
                                        maxTextAreaHeight={210}
                                    />
                                </Box>
                            </Box>
                        </ScrollableBox>

                        {!_.isEmpty(formikBag.values.selectedDiscussionUuid) ? (
                            <Flex>
                                <Box width={1 / 5}></Box>
                                <Box
                                    width={2 / 5}
                                    sx={{ textAlign: 'right', opacity: !formikBag.values.comment ? '60%' : '100%' }}
                                    my={theme.spacing.s}
                                    mr={theme.spacing.xxs}
                                >
                                    <ButtonSecondary
                                        onClick={() => {
                                            clearSelectedDiscussionContent()
                                            formikBag.resetForm({ values: INITIAL_VAUES })
                                        }}
                                        type="button"
                                    >
                                        {newT('button.cancel')}
                                    </ButtonSecondary>
                                </Box>
                                <Box
                                    width={2 / 5}
                                    sx={{ textAlign: 'right', opacity: !formikBag.values.comment ? '60%' : '100%' }}
                                    my={theme.spacing.s}
                                    mr={theme.spacing.xxxs}
                                >
                                    <ButtonPrimary type="submit" themeColor="astronaut">
                                        {newT('button.saveChanges')}
                                    </ButtonPrimary>
                                </Box>
                            </Flex>
                        ) : (
                            <Box sx={{ textAlign: 'right', opacity: !formikBag.values.comment ? '60%' : '100%' }}>
                                <RectangleButton type="submit" color="primary" label={newT('button.comment')} />
                            </Box>
                        )}
                        <ScrollToError />
                        <AutoSaveDiscussionData uuid={submissionProgress.uuid} />
                    </Form>
                )}
            </Formik>
        </Box>
    )
}

export default DiscussSubmission

const ScrollableBox = styled.div`
    box-sizing: border-box;
    overflow-y: scroll;
    overflow-x: hidden;
    background: ${coreColors.white50};
    height: 645px;
    padding: ${spacing[400]};

    &::-webkit-scrollbar {
        width: 4px;
    }
    &::-webkit-scrollbar-thumb {
        background-color: ${coreColors.grey700};
        border-radius: 4px;
    }

    .scrollable-list {
        &::-webkit-scrollbar {
            width: 4px;
        }
        &::-webkit-scrollbar-thumb {
            background-color: ${coreColors.grey700};
            border-radius: 4px;
        }
        .textarea-input-container {
            outline: none;
        }
    }
`
